import React, { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TspCard, TspGauge, TspProgressBar, TspTable, TspEmptyState } from "../../../components/primitives";
import { RESPONSE_RATING_LABEL, RESPONSE_RATINGS } from "../../../constants/tspOperations";

const RATING_COLOR = Object.fromEntries(RESPONSE_RATINGS.map((r) => [r.value, r.color]));
const PAGE_SIZE = 7;

// `response` is GET /api/tsp/stats#public_response (null without
// tsp.public_response.view). `cities` (always present) supplies the per-city
// row: response score and remarks — the "In progress" row is the city the team is currently in, before
// it has any response yet.
export default function PublicResponseSummarySection({ response, cities, subject = "this journey" }) {
  const [page, setPage] = useState(1);

  const formatRemarks = (text, maxWords = 4, maxChars = 22) => {
    if (!text) return "—";
    const trimmed = text.trim();
    const words = trimmed.split(/\s+/);
    if (words.length > maxWords) {
      return words.slice(0, maxWords).join(" ") + "…";
    }
    if (trimmed.length > maxChars) {
      return trimmed.slice(0, maxChars).trim() + "…";
    }
    return trimmed;
  };

  const columns = [
    { key: "city", label: "City", render: (row) => <strong className="tsp-cell-strong tsp-response-city-name">{row.city}</strong> },
    { key: "avgScore", label: "Avg Score", render: (row) => (row.inProgress ? <strong className="tsp-cell-accent">In progress</strong> : <strong className="tsp-cell-accent">{row.averageScore ?? "—"}</strong>) },
    {
      key: "remarks",
      label: "Remarks",
      render: (row) => {
        if (row.inProgress) return <span className="tsp-cell-muted">—</span>;
        if (!row.remarks) return <span className="tsp-cell-muted">—</span>;
        return (
          <span className="tsp-response-remarks-text" title={row.remarks}>
            {formatRemarks(row.remarks)}
          </span>
        );
      },
    },
  ];
  const rows = (cities || []).map((city) => ({
    city: city.city_name,
    averageScore: city.average_score,
    remarks: city.remarks,
    inProgress: (city.visit_state === "current" && !city.responses && !city.remarks) || city.inProgress,
  }));

  if (!response) {
    return (
      <TspCard title="Public Response Summary" className="tsp-title-18">
        <TspEmptyState message="You do not have permission to view public response." />
      </TspCard>
    );
  }

  const breakdown = response.breakdown.map((row) => ({ label: RESPONSE_RATING_LABEL[row.rating], percent: row.percent, color: RATING_COLOR[row.rating] }));
  const totalItems = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const pagedRows = rows.slice(startIndex, startIndex + PAGE_SIZE);

  return (
    <TspCard title="Public Response Summary" className="tsp-title-18" action={<span className="tsp-muted-text tsp-response-count">{response.total} responses</span>}>
      {response.total === 0 ? (
        <TspEmptyState message={`No public response recorded on ${subject} yet.`} />
      ) : (
        <div className="tsp-response-layout">
          <div className="tsp-response-score">
            <TspGauge value={response.average_score} max={response.score_max} label={response.label} color="#10B981" valueColor="#2f3547" inset />
            <div className="tsp-response-breakdown">
              {breakdown.map((row) => (
                <TspProgressBar key={row.label} label={row.label} value={`${row.percent}%`} percent={row.percent} color={row.color} />
              ))}
            </div>
            <p className="tsp-muted-text tsp-response-total">{response.total} total responses</p>
          </div>

          <div className="tsp-response-table-wrapper">
            <TspTable columns={columns} rows={pagedRows} rowKey="city" className="tsp-table-boxed tsp-table-response" />
            {totalPages > 1 && (
              <div className="tsp-response-pagination">
                <span className="tsp-response-pagination-info">
                  Showing {startIndex + 1}–{Math.min(startIndex + PAGE_SIZE, totalItems)} of {totalItems}
                </span>
                <div className="tsp-response-pagination-actions">
                  <button
                    type="button"
                    className="tsp-btn tsp-btn-outline tsp-btn-sm"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={16} aria-hidden="true" /> Previous
                  </button>
                  <span className="tsp-response-page-indicator">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="tsp-btn tsp-btn-outline tsp-btn-sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    aria-label="Next page"
                  >
                    Next <ChevronRight size={16} aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </TspCard>
  );
}
